/* eslint-disable no-param-reassign */
/* eslint-disable no-console */
import { OverlayModule } from '@angular/cdk/overlay';
import { CommonModule } from '@angular/common';
import { Component, DestroyRef, ElementRef, Inject, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { format } from 'date-fns';
import { Observable, Subject, first, map, timer } from 'rxjs';

import { Language } from '../environments/environment.model';
import { AboutComponent } from './components/about/about.component';
import { ChatComponent } from './components/chat/chat.component';
import { CountDownComponent } from './components/count-down/count-down.component';
import { CutCardComponent } from './components/cut-card/cut-card.component';
import { GameSwitchComponent } from './components/game-switch/game-switch.component';
import { ModalComponent } from './components/modal/modal.component';
import { NotificationComponent } from './components/notification/notification.component';
import { StreamingVideoComponent } from './components/streaming-video/streaming-video.component';
import { GameSelectionComponent } from './components/supervisor/game-selection/game-selection.component';
import { ResultChangeComponent } from './components/supervisor/result-change/result-change.component';
import { SupervisorActionsComponent } from './components/supervisor/supervisor-actions/supervisor-actions.component';
import { SupervisorPanelsComponent } from './components/supervisor/supervisor-panels/supervisor-panels.component';
import { EnvironmentResolver } from './core/environment-resolver';
import { DealerServiceToken } from './factories/dealer.factory';
import {
    AppPhase,
    DealerConsoleAction,
    FlagStateType,
    GameType,
    NotificationCode,
    NotificationSeverity,
    SupervisorSubPhase,
    TableMode,
    WorkerAccessLevel
} from './models/communication/app-enums.model';
import {
    AppEventType,
    LoginChangeEvent,
    NewRoundPreparationPhaseChangeEvent,
    NotificationEvent
} from './models/communication/app-events.model';
import { AppState } from './models/communication/app-state.model';
import { CardsChanged } from './models/communication/result-change.model';
import { YEventPayload } from './models/y-protocol.model';
import { BetsStatusComponent } from './phases/bets-status/bets-status.component';
import { BurnCardsComponent } from './phases/burn-cards/burn-cards.component';
import { ErrorPanelComponent } from './phases/error-panel/error-panel.component';
import { NewRoundPreparationComponent } from './phases/new-round-preparation/new-round-preparation.component';
import { RoundVoidedComponent } from './phases/round-voided/round-voided.component';
import { ShoeChangeComponent } from './phases/shoe-change/shoe-change.component';
import { WaitingForCroupierComponent } from './phases/waiting-for-croupier/waiting-for-croupier.component';
import { WaitingForPlayersComponent } from './phases/waiting-for-players/waiting-for-players.component';
import { GameLabelPipe } from './pipes/game-label.pipe';
import { DealerService } from './services/dealer.service';
import { NotificationService } from './services/notification.service';
import { PluginService } from './services/plugin.service';
import { ResultChange } from './services/result-change.service';

@Component({
    standalone: true,
    imports: [
        CommonModule,
        RouterModule,
        BurnCardsComponent,
        WaitingForPlayersComponent,
        ShoeChangeComponent,
        NewRoundPreparationComponent,
        RoundVoidedComponent,
        ErrorPanelComponent,
        OverlayModule,
        AboutComponent,
        NotificationComponent,
        CutCardComponent,
        ModalComponent,
        BetsStatusComponent,
        GameSwitchComponent,
        GameLabelPipe,
        CountDownComponent,
        ChatComponent,
        StreamingVideoComponent,
        ResultChangeComponent,
        WaitingForCroupierComponent,
        SupervisorActionsComponent,
        GameSelectionComponent,
        SupervisorPanelsComponent
    ],
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrl: './app.component.scss'
})
export class AppComponent {
    private static readonly originalWidth = 1920;
    private static readonly originalHeight = 1080;
    private static readonly gameChangeDelayMs = 50;

    @ViewChild('container') containerEl!: ElementRef<HTMLElement>;

    protected viewportScaling = {
        enabled: true,
        fillWidth: true,
        fillHeight: true
    };

    protected containerHeight = AppComponent.originalHeight;
    protected containerWidth = AppComponent.originalWidth;

    protected appIsHidden = false;
    protected aboutDialogVisible = false;
    protected menuVisible = false;
    protected supervisorLogoutMode = false;

    protected time$: Observable<string> = this.createTimeObservable();
    protected state$!: Observable<AppState>;
    protected version: string;
    protected languages: Language[] = [];

    protected isVideoHidden = false;
    protected cutCardDrawn = false;

    protected readonly AppPhase = AppPhase;
    protected readonly TableMode = TableMode;
    protected readonly WorkerAccessLevel = WorkerAccessLevel;
    protected readonly SupervisorSubPhase = SupervisorSubPhase;
    protected readonly DealerConsoleAction = DealerConsoleAction;

    protected readonly restartCountdownSubject = new Subject<void>();
    protected restartCountdown$ = this.restartCountdownSubject.asObservable();

    constructor(
        private readonly environment: EnvironmentResolver,
        private readonly route: ActivatedRoute,
        private readonly pluginService: PluginService,
        private readonly destroyRef: DestroyRef,
        protected readonly resultChange: ResultChange,
        protected readonly notification: NotificationService,
        @Inject(DealerServiceToken) protected readonly dealerService: DealerService
    ) {
        const { version, languages } = this.environment.getEnvironment();
        this.version = version;
        this.languages = languages || [];

        this.listenToDealerService();
        this.dealerService.start();
    }

    // SUPERVISOR

    protected handleSupervisorLogout(): void {
        this.supervisorLogoutMode = false;

        if (this.dealerService.getState().roundState.phase === AppPhase.BetsOpen) {
            // After we logout in BetsOpen phase, the countdown restarts.
            this.restartCountdown();
        }
    }

    protected async selectGame({ button, game }: { button: HTMLButtonElement; game: GameType }): Promise<void> {
        await this.onButtonClick(button, async () => {
            await this.dealerService.sendChangeGameRequest(game);
        });
    }

    protected async sendAction({ button, action }: { button: HTMLButtonElement; action: DealerConsoleAction }): Promise<void> {
        await this.onButtonClick(button, async () => {
            await this.dealerService.sendDealerActionRequest(action);
        });
    }

    protected async cancelSupervisorAction(button: HTMLButtonElement): Promise<void> {
        if (this.dealerService.getState().flagsState.resultChangingRequested) {
            // Because we confirmed the result change which modified the cards,
            // we need to explicitly revert them to the original values.
            this.resultChange.revertCardValues();
        }

        await this.onButtonClick(button, async () => {
            await this.dealerService.sendCancelSupervisorActionRequest();
        });
    }

    protected async confirmResultChange({ button, data }: { button: HTMLButtonElement; data: CardsChanged }): Promise<void> {
        await this.onButtonClick(button, async () => {
            await this.dealerService.sendResultChangeRequest(data);
        });
    }

    protected async toggleChat(button: HTMLButtonElement, toggle?: boolean): Promise<void> {
        await this.onButtonClick(button, async () => {
            await this.dealerService.sendSetFlagStateRequest(FlagStateType.ChatEnabled, !toggle);
        });
    }

    protected changeLanguage(languageCode: string): void {
        window.location.href = `/${languageCode}/${window.location.search}`;
    }

    protected restartCountdown(): void {
        this.restartCountdownSubject.next();
    }

    protected scaleContainer(event: UIEvent): void {
        if (this.viewportScaling.enabled) {
            this.scaleViewport((event.target as Window).innerWidth, (event.target as Window).innerHeight);
        }
    }

    private handleNotification(event: NotificationEvent): void {
        // Don't show a notification after scanning cards as
        // it temporarily covers which is annoying for the user.
        if (event.code === NotificationCode.AuthenticationSuccess) {
            return;
        }

        const canClose = event.severity !== NotificationSeverity.Info;
        this.notification.show(event.code, event.severity, canClose);
    }

    private listenToDealerService(): void {
        this.state$ = this.dealerService.getStateObservable();
        this.state$.pipe(first()).subscribe(() => {
            // Timeout because - TypeError: Cannot read properties of undefined (reading 'nativeElement')
            if (this.viewportScaling.enabled) {
                setTimeout(() => {
                    this.scaleViewport(window.innerWidth, window.innerHeight);
                });
            }
        });

        this.dealerService
            .getEventsObservable()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((event) => {
                this.handleDealerEvent(event);
                this.callPlugins(event);
            });
    }

    private handleDealerEvent(event: YEventPayload<AppEventType, unknown>): void {
        if (event.name === AppEventType.LoginChange) {
            if ((event.data as LoginChangeEvent).old?.accessLevel === WorkerAccessLevel.Supervisor) {
                this.handleSupervisorLogout();
            }
        } else if (event.name === AppEventType.CutCardDrawn) {
            this.cutCardDrawn = true;
            this.handleCutCardDrawnEvent();
        } else if (event.name === AppEventType.Notification) {
            this.handleNotification(event.data as NotificationEvent);
        } else if (
            this.cutCardDrawn &&
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            (event.data as NewRoundPreparationPhaseChangeEvent).phase === AppPhase.NewRoundPreparation
        ) {
            this.cutCardDrawn = false;
            this.notification.hide();
        }
    }

    private callPlugins(event: YEventPayload<AppEventType, unknown>): void {
        for (const plugin of this.pluginService.plugins) {
            plugin(this.dealerService.getState(), event, this.containerEl.nativeElement);
        }
    }

    private async onButtonClick(button: HTMLButtonElement, callback: () => Promise<void>): Promise<void> {
        try {
            button.disabled = true;
            await callback();
            button.disabled = false;
        } catch (error) {
            console.error(error);
            button.disabled = false;
            this.notification.show(NotificationCode.ActionFailed, NotificationSeverity.Error, true);
        }
    }

    private createTimeObservable(): Observable<string> {
        // eslint-disable-next-line @typescript-eslint/no-magic-numbers
        return timer(0, 1000).pipe(map(() => format(new Date(), 'HH:mm:ss')));
    }

    private handleCutCardDrawnEvent(): void {
        this.notification.show(NotificationCode.CutCardDrawn, NotificationSeverity.Error, true);
    }

    private scaleViewport(innerWidth: number, innerHeight: number): void {
        const originalProportion = AppComponent.originalWidth / AppComponent.originalHeight;

        const windowProportion = innerWidth / innerHeight;
        const proportionRatio = windowProportion / originalProportion;

        const scale = proportionRatio < 1 ? innerWidth / AppComponent.originalWidth : innerHeight / AppComponent.originalHeight;

        if (this.viewportScaling.fillWidth) {
            this.containerWidth = Math.max(proportionRatio, 1) * AppComponent.originalWidth;
        }

        if (this.viewportScaling.fillHeight) {
            this.containerHeight = AppComponent.originalHeight + AppComponent.originalHeight * (1 - Math.min(proportionRatio, 1));
        }

        this.containerEl.nativeElement.style.transform = `scale(${scale})`;
        this.containerEl.nativeElement.style.width = `${this.containerWidth}px`;
        this.containerEl.nativeElement.style.height = `${this.containerHeight}px`;
    }
}
